import moment from 'moment';

import set from 'lodash/set';
import find from 'lodash/find';
import includes from 'lodash/includes';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import padStart from 'lodash/padStart';
import range from 'lodash/range';

import { handleActions } from 'redux-actions';

import { change, getFormValues, SubmissionError } from 'redux-form';

import http from '../../../shared/http';
import endpoints from '../../../../config/endpoints';

import { createSelectOptionsFromSysEnums } from '../../../shared/form-elements/select';

import { actions as rootActions } from  '../../../../modules/root';

import { enum as affiliateRanks } from '../../../../../../../enums/affiliate-ranks';

export const namespace = 'manualMailingsEditor';

const isRocket = process.env.CPA_ENV === 'rocket';

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const SET_INITIAL_STATE = `${namespace}/set-initial-state`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const TOGGLE_TEST_EMAIL_MODAL = `${namespace}/toggle-test-email-modal`;
const FETCH_ADMINS_LOOKUPS_SUCCESS = `${namespace}/fetch-admins-lookups-success`;
const FETCH_ADMINS_LOOKUPS_ERROR = `${namespace}/fetch-admins-lookups-error`;
const SEND_TEST_EMAIL_SUCCESS = `${namespace}/send-test-email-success`;
const SEND_TEST_EMAIL_ERROR = `${namespace}/send-test-email-error`;

const segmentNames = [
	{ value: null, label: 'All' },
	...(isRocket ? [
		{ value: 'ranks', label: 'Ranks' }
	] : [
		{ value: 'internal-statuses', label: 'Internal statuses' }
	])
];

const initialData = {
	from: isRocket ? 'RocketProfit <info@rocketprofit.com>' : 'Everad <info@everad.com>',
	language: [{ value: 'ru', label: 'Russian' }],
	segmentName: [segmentNames[0]],
	segments: [],
	templates: [{ id: null, title: '', text: '' }],
	date: moment(),
	time: [{ value: null, label: 'Now' }],
	timezone: [{ value: 'Europe/Moscow', label: 'Europe/Moscow' }],
	sentAt: null
};

// initial state
const initialState = {
	data: initialData,
	isLoading: true,
	isError: false,
	showTestEmailModal: false,
	testEmailModalIndex: null
};

function getCreateMailingRequest(payload, getState) {
	const values = getFormValues(`${namespace}Form`)(getState());

	return http({
		url: endpoints.MAILINGS_NEW.MANUAL_MAILINGS,
		method: 'post',
		actions: [SET_INITIAL_STATE, FETCH_DATA_ERROR],
		data: payload,
		addToResponse: { values }
	});
}

function getUpdateMailingRequest(id, payload, getState) {
	const values = getFormValues(`${namespace}Form`)(getState());

	return http({
		url: `${endpoints.MAILINGS_NEW.MANUAL_MAILINGS}/${id}`,
		method: 'put',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		data: payload,
		addToResponse: { values, getState }
	});
}

// actions
export const actions = {
	own: {
		fetchData: props => (dispatch, getState) => {
			const id = props.params.id;

			if (!id) {
				return dispatch({ type: SET_INITIAL_STATE });
			}

			const request = http({
				url: `${endpoints.MAILINGS_NEW.MANUAL_MAILINGS}/${id}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: { getState }
			});

			return request(dispatch);
		},
		onSegmentNameChange: () => (dispatch, getState) => {
			dispatch(change(`${namespace}Form`, 'segments', []));
		},
		onDateChange: (e, value, prevValue) => (dispatch, getState) => {
			const values = getFormValues(`${namespace}Form`)(getState());

			const isNowSelected = !values.time[0].value;

			if (!value.isSame(prevValue)) {
				if (value.isSame(moment(), 'day')) {
					dispatch(change(`${namespace}Form`, 'time', [{ value: null, label: 'Now' }]));
				} else if (isNowSelected) {
					dispatch(change(`${namespace}Form`, 'time', [{ value: '00:00', label: '00:00' }]));
				}
			}
		},
		onSubmit: (action, values, _, props) => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());

			const segmentName = values.segmentName[0].value;
			const segments = segmentName && [{
				key: segmentName,
				values: values.segments.map((s) => s.value)
			}];

			const time = values.time[0].value;
			const timezone = values.timezone[0].value;

			const sendAt = time
				? moment(values.date)
					.tz(timezone)
					.set({
						h: time.split(':')[0],
						m: time.split(':')[1],
						s: 0
					})
				: moment();

			const payload = {
				from: values.from,
				language: values.language[0].value,
				segments,
				templates: values.templates,
				send_at: action === 'save'
					? null
					: sendAt.utc().format(),
				timezone: values.timezone[0].value
			};

			const id = props.params.id;

			const request = id
				? getUpdateMailingRequest(id, payload, getState)
				: getCreateMailingRequest(payload, getState);

			return request(dispatch).then((action) => {
				if (action.type === FETCH_DATA_ERROR && action.payload.response.status === 400) {
					throw new SubmissionError(prepareFormErrors(action.payload.response.data));
				}
			});
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),
		toggleTestEmailModal: () => ({
			type: TOGGLE_TEST_EMAIL_MODAL,
			payload: { index: null }
		}),
		openTestEmailModal: (index) => ({
			type: TOGGLE_TEST_EMAIL_MODAL,
			payload: { index }
		}),
		sendTestEmail: (data) => (dispatch, getState) => {
			const values = getFormValues(`${namespace}Form`)(getState());

			const index = selectors.select(getState(), 'testEmailModalIndex');

			const template = values.templates[index];

			const payload = {
				email: data.email[0].label,
				title: template.title,
				from: values.from,
				language: values.language[0].value,
				text: template.text
			};

			const request = http({
				url: endpoints.MAILINGS_NEW.SEND_TEST,
				method: 'post',
				actions: [SEND_TEST_EMAIL_SUCCESS, SEND_TEST_EMAIL_ERROR],
				data: payload
			});

			dispatch(actions.own.toggleTestEmailModal());

			dispatch(rootActions.spinnerToggle());

			return request(dispatch).then((action) => {
				dispatch(rootActions.spinnerToggle());

				if (action.type === SEND_TEST_EMAIL_ERROR && action.payload.response.status === 400) {
					throw new SubmissionError(prepareFormErrors(action.payload.response.data));
				}
			});
		},
		timezonesLookup: text => (dispatch, getState) => {
			const timezones = getState().root.lists.timezones;

			const filtered = text
				? timezones.filter((item) => includes(item.text.toLowerCase(), text.toLowerCase()))
				: timezones;

			return Promise.resolve({ options: createSelectOptionsFromSysEnums(filtered.slice(0, 4)) });
		},
		adminsLookup: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.ADMINS}?query=${text}`,
				method: 'get',
				actions: [FETCH_ADMINS_LOOKUPS_SUCCESS, FETCH_ADMINS_LOOKUPS_ERROR]
			});

			return request(dispatch).then((action) => {
				if (action.type === FETCH_ADMINS_LOOKUPS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		}
	}
};

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: mapResponseDataToStateData(action.payload.data, action.meta),
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			data: action.meta.values,
			isLoading: false,
			isError: true
		}),
		[SET_INITIAL_STATE]: (state, action) => ({
			...initialState,
			isLoading: false
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[TOGGLE_TEST_EMAIL_MODAL]: (state, action) => ({
			...state,
			showTestEmailModal: !state.showTestEmailModal,
			testEmailModalIndex: action.payload.index
		})
	}
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getOptions: (state) => {
		const values = getFormValues(`${namespace}Form`)(state);

		return {
			languages: createSelectOptionsFromSysEnums(state.root.lists['languages']),
			timezones: createSelectOptionsFromSysEnums(state.root.lists['timezones']),
			segmentNames,
			segments: values ? getSegments(values.segmentName[0].value, state.root.lists) : [],
			times: values ? getTimeOptions(values) : []
		};
	},
	getTestEmailFormInitialValues: (state) => ({
		email: [{
			label: state.root.data.email,
			value: state.root.data.id
		}]
	})
};

function mapResponseDataToStateData(data, { getState }) {
	const state = getState();

	const options = selectors.getOptions(state);

	const date = data.send_at && moment(data.send_at);
	const time = date && date.format('HH:mm');

	const segment = data.segments && find(data.segments, { key: isRocket ? 'ranks' : 'internal-statuses' });

	const segmentName = segment
		? [find(options.segmentNames, { value: segment.key })]
		: [options.segmentNames[0]];

	const segmentOptions = getSegments(segmentName[0].value, state.root.lists);

	const segments = segment ? segment.values.map((value) => find(segmentOptions, { value })) : [];

	return {
		from: data.from,
		language: [find(options.languages, { value: data.language })],
		segmentName,
		segments,
		templates: data.templates,
		date: date || moment(),
		time: [{ value: time, label: time || 'Now' }],
		timezone: [find(options.timezones, { value: data.timezone })],
		sentAt: data.sent_at && moment(data.sent_at)
	};
}

function getSegments(segmentName, lists) {
	switch (segmentName) {
		case 'ranks':
			return affiliateRanks.map((r) => ({ value: r.id, label: r.name }));
		case 'internal-statuses':
			return createSelectOptionsFromSysEnums(lists['affiliate-internal-statuses']);
		default:
			return [];
	}
}

const times = range(0, 24)
	.map((h) => padStart(h, 2, '0'))
	.reduce((acc, h) => [...acc, [h, '00'], [h, '30']], []);

function getTimeOptions(values) {
	const timezone = values.timezone[0].value;

	const date = moment(values.date).tz(timezone);
	const now = moment().tz(timezone);

	const options = times
		.filter(([h, m]) => moment(date).set({ h, m, s: 0 }).isAfter(now))
		.map(([h, m]) => ({ value: `${h}:${m}`, label: `${h}:${m}` }));

	if (date.isSame(now, 'day')) {
		options.unshift({ value: null, label: 'Now' });
	}

	return options;
}

const prepareFormErrors = errors => {
	const validationsErrors = {};
	forEach(errors, value => {
		const key = value.dataPath.slice(1);
		set(validationsErrors, key, value.message);
	});
	return validationsErrors;
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/mailings-new/manual-mailings/editor/index.js